import { Tab } from '@headlessui/react'
import { Formik, FormikHelpers } from 'formik'
import { isArray } from 'lodash'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import CallApi from 'shared/api/call/Api'
import {
  SelectedOperatorType,
  SubmitCallcenterFormResponse,
  SubmitPriceInquiryFormRequestDto,
  SubmitSalesPersonFollowUpRequestDto,
  SubmitSalesPersonOthersRequestDto,
  SubmitSalesPersonTransferRequestDto,
  SubmittedNote,
} from 'shared/api/call/ApiGlobals'
import { SalesPersonProductResponse } from 'shared/api/manager/ApiGlobals'
import { useLoggedInUser } from 'shared/contexts/useLoggedInUser'
import {
  resetCallData,
  resetFormData,
  setCallData,
  setShowCallRegistration,
} from 'store/slice/call-detail/call-detail.slice'
import { RootState } from 'store/store'

import { SelectedCustomerType } from '../../../store/slice/call-detail/types'
import ContactInfoCard from './components/ContactInfoCard'
import FactorTarkibiModal from './components/FactorTarkibiModal'
import LeftToolbar from './components/LeftToolbar'
import Sidebar from './components/Sidebar'
import SubmitButtons from './components/SubmitButtons'
import { SoodTypeEnum } from './components/TabPanels/PriceInquiry/PriceInquiryTable/SoodTypeSelect'
import TabPanelsContainer from './components/TabPanels/TablPanelsContainer'
import TabsList from './components/TabsList'
import ModalOpportunity from './ModalOpportunity'

export type CreatedOp = {
  name?: string
  id?: string
}

export type formikValuesType = {
  transferredBusinessUnit?: string
  notes?: SubmittedNote[]
  selectedOperator?: SelectedOperatorType
  subject?: string
  opportunityId?: string
  companyName?: string
  shouldResetForm?: boolean
  name: { name: string; position: string }
  preName: { label: string; value: number }
  lead?: SelectedCustomerType | undefined
  leadSource?: number
  phoneNumbers: string[]
  companyPhone?: string[]
  purchaseHistory?: number
  callForPriceCount?: number
  isVip?: boolean
  vipBroker?: string
  city?: {
    city?: string
    state?: string
    label?: string
  }
  description?: string
  financial: {
    remainingAmount?: string
    creditBalance?: string
    possibilityOfPrePayment?: boolean
  }
  happyCallInfo: {
    recentStatus?: string
  }
  callType: 'other' | 'transfer'
  isQuoteRequested?: string
  products: (SalesPersonProductResponse & {
    quantity?: number
    wage?: string
    pricePerUnit?: number
    wageType?: SoodTypeEnum
  })[]
  isNewCustomer?: boolean
  customerRequestInfo?: { title: string; amount: string; unit: string }[]
}

function SalesPerSonCallSubmission() {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0)
  const [factorTarkibiModalIsOpen, setFactorTarkibiModalIsOpen] =
    useState(false)
  const [createdOps, setCreatedOps] = useState<CreatedOp[]>([])
  const [opModalIsOpen, setOpModalIsOpen] = useState(false)

  const callDetail = useSelector((state: RootState) => state.callDetail)
  const isHangup = useSelector(
    (state: RootState) => state.callDetail.callData.isHangup
  )
  const { loggedInUser } = useLoggedInUser()
  const dispatch = useDispatch()
  const phoneNumbers =
    callDetail.callData.phoneNumbers?.filter((item) => item.startsWith('09')) ||
    []
  const companyPhone =
    callDetail.callData.phoneNumbers?.filter(
      (item) => !item.startsWith('09')
    ) || []

  const initialValues: formikValuesType = {
    notes: [],
    opportunityId: callDetail.callData.opportunityId || '',
    isQuoteRequested: '1',
    shouldResetForm: false,
    leadSource: callDetail.callData.leadSource?.value || 100000005,
    name: {
      name: '',
      position: '',
    },
    description: '',
    companyName: callDetail.callData.companyName || '',
    companyPhone: companyPhone.length ? companyPhone : [''],
    phoneNumbers: phoneNumbers.length ? phoneNumbers : [''],
    preName: {
      label: callDetail.callData.preName?.label || 'آقای',
      value: callDetail.callData.preName?.value || 100000004,
    },
    financial: {
      creditBalance: '',
      possibilityOfPrePayment: false,
      remainingAmount: '',
    },
    happyCallInfo: {
      recentStatus: '',
    },
    isVip: callDetail.callData.isVIP || false,
    callForPriceCount: 0,
    purchaseHistory: 0,
    vipBroker: '',
    callType: 'other',
    lead: {
      name: callDetail.callData.leadName || '',
      type: callDetail.callData.accountId
        ? 'account'
        : callDetail.callData.contactId
        ? 'contact'
        : 'lead',
      id:
        callDetail.callData.accountId ||
        callDetail.callData.contactId ||
        callDetail.callData.leadId ||
        '',
      companyName: '',
    },
    products: [],
    customerRequestInfo: [
      {
        title: '',
        amount: '',
        unit: '',
      },
    ],
  }

  const handleSubmit = async (
    values: formikValuesType,
    formikHerlpers: FormikHelpers<formikValuesType>
  ) => {
    const payload: Partial<SubmitPriceInquiryFormRequestDto> &
      Partial<SubmitSalesPersonOthersRequestDto> &
      Partial<SubmitSalesPersonFollowUpRequestDto> &
      Partial<SubmitSalesPersonTransferRequestDto> = {
      description: values.description || '',
      isQuoteRequested: false,
      phoneCallId: callDetail?.callData?.phoneCallId,
      opportunityId: values.opportunityId || '',
    }

    if (values.transferredBusinessUnit)
      payload.transferredBusinessUnit = values.transferredBusinessUnit
    if (values.products?.length > 0)
      payload.products = values.products.map((node) => ({
        productId: node.id || '',
        productName: node.name || '',
        unitId: node.unitId || '',
        unitName: node.unit || '',
        quantity: node.quantity || 0,
        pricePerUnit: node?.pricePerUnit || 0,
      }))
    if (values.city?.label) payload.cityOfEvacuation = values?.city.label
    if (values.isQuoteRequested === '0') payload.isQuoteRequested = true
    if (values.preName?.value) payload.preName = values.preName.value
    if (values.companyName) payload.companyName = values.companyName
    if (values.leadSource) payload.leadSource = values.leadSource
    if (values.companyPhone) payload.companyPhone = values.companyPhone
    if (values.phoneNumbers) payload.phoneNumber = values.phoneNumbers
    if (values.lead?.type && values.lead?.id)
      payload.selectedCustomer = {
        type: values.lead.type as any,
        id: values.lead?.id,
      }
    if (values.lead?.name) payload.newLeadName = values.lead.name
    if (!values.lead?.id) payload.isNewCustomer = true
    if (loggedInUser?.userPhone) payload.extension = loggedInUser.userPhone
    if (!values.preName?.value) payload.preName = values.preName.value
    if (values.subject) payload.subject = values.subject
    if (
      (values?.subject === 'تناژ درخواستی پایین است' ||
        values?.subject === 'عدم موجودی کالا') &&
      values?.customerRequestInfo?.length &&
      values?.customerRequestInfo[0].title
    ) {
      payload.description = ''
      values?.customerRequestInfo.forEach((node) => {
        payload.description += `${node.title}/${node?.amount}/${node?.unit}\n`
      })
    }

    let submitRequest: any = null
    if (selectedTabIndex === 0) {
      if (factorTarkibiModalIsOpen)
        payload.selectedOperator = values.selectedOperator
      submitRequest =
        CallApi.callcenter.callCenterControllerSubmitPriceInquiryForm
    } else if (selectedTabIndex === 1) {
      payload.notes = values.notes?.filter((item) => item) || []
      submitRequest =
        CallApi.callcenter.callCenterControllerSubmitSalesPersonFollowUp
    } else if (selectedTabIndex === 3) {
      submitRequest =
        CallApi.callcenter.callCenterControllerSubmitSalesPersonOthers
    } else if (selectedTabIndex === 2) {
      payload.selectedOperator = values.selectedOperator
      submitRequest =
        CallApi.callcenter.callCenterControllerSubmitSalesPersonTransfer
    }

    try {
      if (submitRequest) {
        const response: SubmitCallcenterFormResponse = await submitRequest(
          payload as any
        ).then((res: any) => res.data)

        if (!response.isError) {
          if (factorTarkibiModalIsOpen) setFactorTarkibiModalIsOpen(false)
          if (values.shouldResetForm) {
            if (response.opportunityId) {
              setCreatedOps((state) => [
                ...state,
                {
                  name: response.opportunityName,
                  id: response.opportunityId,
                },
              ])
            }
            dispatch(
              setCallData({
                ...callDetail.callData,
                companyName: values.companyName,
                phoneNumbers: [
                  ...(payload.phoneNumber || []),
                  ...(payload.companyPhone || []),
                ],
                preName: {
                  label: values.preName.label,
                  value: values.preName.value,
                },
                leadSource: {
                  value: payload.leadSource,
                },
                leadName: values.lead?.name,
                [`${values.lead?.type}Id`]: values.lead?.id,
              } as any)
            )
            formikHerlpers.resetForm()
          } else {
            dispatch(resetFormData())
            dispatch(resetCallData())
            if (response.opportunityId) {
              setOpModalIsOpen(true)
              setCreatedOps((state) => [
                ...state,
                {
                  name: response.opportunityName,
                  id: response.opportunityId,
                },
              ])
            } else {
              dispatch(setShowCallRegistration(false))
            }
          }
        } else {
          toast.error(response.massage)
        }
      } else if (selectedTabIndex === 0) {
      }
    } catch {
      toast.error('مشکل در ارتباط با سرور')
    }

    return Promise.resolve(null)
  }
  const validate = (formValues: formikValuesType) => {
    const errors: any = { customerRequestInfo: [], description: '' }
    const errorMessage = 'فیلد اجباریست'

    if (selectedTabIndex === 3) {
      if (
        formValues?.subject === 'تناژ درخواستی پایین است' ||
        formValues?.subject === 'عدم موجودی کالا'
      ) {
        let errorObj: any = {}

        formValues.customerRequestInfo?.map((node, index) => {
          if (!node.amount) errorObj = { ...errorObj, amount: errorMessage }
          if (!node.title) errorObj = { ...errorObj, title: errorMessage }
          if (!node.unit) errorObj = { ...errorObj, unit: errorMessage }
          if (Object.entries(errorObj).length)
            errors.customerRequestInfo[index] = errorObj
        })
      } else if (
        formValues?.subject === 'تماس جهت مشاوره' ||
        formValues?.subject === 'دلایل دیگر'
      ) {
        if (!formValues?.description) errors.description = errorMessage
      }
    }

    Object.entries(errors).map((node) => {
      if (isArray(node[1]) && !node[1].length) delete errors[node[0]]
      else if (!node[1]) delete errors[node[0]]
    })

    return errors
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={validate}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        validateOnMount={false}
      >
        {({ values }) => (
          <div
            id="submit-sales-form-id"
            className="w-full relative pr-[90px] xl:pr-[182px] pt-[20px] pl-[80px] pb-[26px] bg-[#E5E5E5] min-h-screen"
          >
            <div className="flex">
              <Sidebar transferHistory={callDetail.callData?.transferHistory} />
              <div className={'mx-auto w-[100%]'}>
                <div className={'flex justify-center gap-x-6'}>
                  <div
                    className={
                      'hidden w-0 h-[845px] xl:!min-w-[310px] xl:max-w-[310px] xl:block'
                    }
                  >
                    <ContactInfoCard />
                  </div>
                  <div className="w-full h-[100%] max-w-[1312px] min-w-[1120px] ">
                    <Tab.Group
                      selectedIndex={selectedTabIndex}
                      onChange={setSelectedTabIndex}
                    >
                      <TabsList />
                      <TabPanelsContainer />
                    </Tab.Group>
                  </div>
                </div>
                <div className={'flex max-w-[1646px] w-full mx-auto'}>
                  <SubmitButtons
                    disabledCombinedConnection={values.shouldResetForm === true}
                    submitButtonLabel={
                      selectedTabIndex === 0 ? 'ثبت آپورچونیتی' : 'ثبت تماس'
                    }
                    disableSubmitButton={selectedTabIndex === 2 && isHangup}
                    hasOtherRequest={true}
                    handleFactorTarkibiButtonClick={
                      selectedTabIndex === 0
                        ? () => setFactorTarkibiModalIsOpen(true)
                        : undefined
                    }
                  />
                </div>
              </div>
            </div>
            <LeftToolbar
              defaultPhonNumber={
                values?.phoneNumbers?.[0] ||
                callDetail?.ringingData?.phoneNumber
              }
            />

            <FactorTarkibiModal
              isVisible={factorTarkibiModalIsOpen}
              onClose={() => setFactorTarkibiModalIsOpen(false)}
            />
          </div>
        )}
      </Formik>
      <ModalOpportunity
        onClose={() => {
          setOpModalIsOpen(false)
          setTimeout(() => {
            setCreatedOps([])
          }, 300)
          dispatch(setShowCallRegistration(false))
        }}
        isVisible={opModalIsOpen}
        ops={createdOps}
      />
    </>
  )
}

export default SalesPerSonCallSubmission
